import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@chakra-ui/react';
import { heroImageSet } from '../../utils/responsiveImage.mjs';
import './SubPageHero.css';

// Route an in-app path through React Router (no full reload); leave external
// URLs / anchors as plain <a> hrefs.
const ctaRouteProps = (action) => {
  if (!action?.href) return {};
  if (action.href.startsWith('/') && !action.href.startsWith('//')) {
    return { as: Link, to: action.href };
  }
  return { as: 'a', href: action.href };
};

/**
 * Reusable compact hero for sub-pages (product pages, feature pages, etc.).
 * Keeps a short height so users get to main content quickly.
 *
 * @param {string} [eyebrow] - Small label above the title (e.g. product name)
 * @param {string} title - Main headline (rendered as h1)
 * @param {string} [subtitle] - Optional supporting line below the title
 * @param {string[]} [badges] - Optional list of pill labels (e.g. ["Antimicrobial", "Pro-healing"])
 * @param {{ label: string, href?: string, onClick?: () => void }} [cta] - Optional primary CTA button
 * @param {{ label: string, href?: string, onClick?: () => void }} [secondaryCta] - Optional secondary (outline) CTA button
 * @param {string} [backgroundImage] - Optional background image URL (maroon overlay still applied)
 */
export default function SubPageHero({ eyebrow, title, subtitle, badges = [], cta, secondaryCta, backgroundImage }) {
  // A background-image can't use <picture>, so the AVIF/WebP swap goes through
  // image-set() instead — format negotiation only, no width switching.
  //
  // These are the two *inputs*; SubPageHero.css picks between them and assigns
  // the winner to `--sub-page-hero-bg-image` (what the background shorthands in
  // SubPageHero.css and BioPly.css actually read) behind an @supports test.
  // The choice has to live in the stylesheet rather than here because inline
  // custom properties outrank stylesheet ones — writing the fallback to
  // `--sub-page-hero-bg-image` directly would make the @supports upgrade a
  // no-op on every browser.
  const heroBgStyle = backgroundImage
    ? {
        '--sub-page-hero-bg-url': `url("${backgroundImage}")`,
        '--sub-page-hero-bg-image-set': heroImageSet(backgroundImage),
      }
    : undefined;

  const makeCtaHandler = (action) => (e) => {
    if (action?.href?.startsWith('#')) {
      e.preventDefault();
      const el = document.querySelector(action.href);
      el?.scrollIntoView({ behavior: 'smooth' });
    }
    action?.onClick?.(e);
  };

  return (
    <section
      className={`sub-page-hero${backgroundImage ? ' sub-page-hero--with-bg' : ''}`}
      aria-label="Page header"
      style={heroBgStyle}
    >
      <div className="sub-page-hero-inner">
        {eyebrow && <p className="sub-page-hero-eyebrow">{eyebrow}</p>}
        <h1 className="sub-page-hero-title">{title}</h1>
        {subtitle && <p className="sub-page-hero-subtitle">{subtitle}</p>}
        {badges.length > 0 && (
          <div className="sub-page-hero-badges">
            {badges.map((badge) => (
              <span key={badge} className="sub-page-hero-badge">
                {badge}
              </span>
            ))}
          </div>
        )}
        {(cta?.label || secondaryCta?.label) && (
          <div className="sub-page-hero-cta">
            {cta?.label && (
              <Button
                {...ctaRouteProps(cta)}
                variant="secondary"
                bg="white"
                color="maroon.800"
                size="lg"
                fontWeight={700}
                borderRadius="full"
                px={7}
                boxShadow="0 12px 28px -12px rgba(0, 0, 0, 0.55)"
                _hover={{ bg: 'whiteAlpha.900', transform: 'translateY(-1px)' }}
                _active={{ transform: 'translateY(0)' }}
                onClick={makeCtaHandler(cta)}
              >
                {cta.label}
              </Button>
            )}
            {secondaryCta?.label && (
              <Button
                {...ctaRouteProps(secondaryCta)}
                variant="outline"
                borderColor="whiteAlpha.700"
                color="white"
                bg="whiteAlpha.100"
                size="lg"
                fontWeight={700}
                borderRadius="full"
                px={7}
                sx={{ backdropFilter: 'blur(4px)' }}
                _hover={{ bg: 'whiteAlpha.300', borderColor: 'white', transform: 'translateY(-1px)' }}
                _active={{ transform: 'translateY(0)' }}
                onClick={makeCtaHandler(secondaryCta)}
              >
                {secondaryCta.label}
              </Button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
